/**
 * PortfolioContribution — stacked area chart showing each thesis's
 * contribution to total portfolio return over time.
 */
import { useEffect, useState } from 'react';
import Plot from 'react-plotly.js';
import * as api from '../lib/api';

const COLORS = ['#6366f1', '#f59e0b', '#22c55e', '#ef4444', '#8b5cf6', '#06b6d4', '#ec4899', '#14b8a6'];

interface Props {
  thesisIds: string[];
  thesisNames: Record<string, string>;
}

export function PortfolioContribution({ thesisIds, thesisNames }: Props) {
  const [data, setData] = useState<Record<string, api.ThesisPerfResponse>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (thesisIds.length === 0) {
      setLoading(false);
      return;
    }
    setLoading(true);
    Promise.all(
      thesisIds.map((id) =>
        api.portfolio.thesisPerformance(id, 'all')
          .then((r) => [id, r] as const)
          .catch(() => [id, null] as const)
      ),
    ).then((results) => {
      const map: Record<string, api.ThesisPerfResponse> = {};
      for (const [id, r] of results) {
        if (r && r.points.length > 0) map[id] = r;
      }
      setData(map);
    }).finally(() => setLoading(false));
  }, [thesisIds.join(',')]);

  if (loading) return <div className="text-xs text-ink-mute">Loading contribution data...</div>;
  if (Object.keys(data).length === 0) {
    return (
      <div className="card p-4 text-xs text-ink-mute" data-testid="contribution-empty">
        No contribution data yet. Open theses with performance snapshots will appear here.
      </div>
    );
  }

  const traces: Plotly.Data[] = [];
  let idx = 0;
  for (const [id, perf] of Object.entries(data)) {
    traces.push({
      x: perf.points.map((p) => p.date),
      // AE-010 anti-hallucination Layer 4: null NAV stays undefined so
      // the stacked area shows a gap rather than collapsing to zero.
      y: perf.points.map((p) => p.nav ?? undefined) as Plotly.Datum[],
      type: 'scatter',
      mode: 'lines',
      name: thesisNames[id] || id,
      stackgroup: 'one',
      line: { color: COLORS[idx % COLORS.length] },
      fillcolor: COLORS[idx % COLORS.length] + '33',
      connectgaps: false,
    });
    idx++;
  }

  return (
    <div className="card p-4" data-testid="portfolio-contribution">
      <h3 className="text-sm font-semibold mb-3">Thesis NAV Contribution</h3>
      <Plot
        data={traces}
        layout={{
          height: 280,
          margin: { t: 10, b: 40, l: 60, r: 20 },
          xaxis: { showgrid: false },
          yaxis: { title: { text: 'NAV ($)' }, tickformat: '$,.0f', showgrid: true, gridcolor: '#e2e8f0' },
          legend: { orientation: 'h', y: -0.2 },
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          font: { size: 11, color: '#64748b' },
        }}
        config={{ displayModeBar: false, responsive: true }}
        style={{ width: '100%' }}
      />
    </div>
  );
}
